import {request} from './client.js'
import {toEpochMs} from '../utils/time.js'

/**
 * Map UI-range chips to backend resolutions (seconds) and `from` cutoffs.
 * Backend supports: 300, 900, 1800, 3600, 7200, 14400, 43200, 86400, 259200, 604800, 1209600.
 * @type {Record<import('./types.js').MetricRange, {resolution: number, spanMs: number}>}
 */
const RANGE_MAP = {
    '7d':   {resolution: 3600,   spanMs: 7 * 24 * 60 * 60 * 1000},
    '30d':  {resolution: 14400,  spanMs: 30 * 24 * 60 * 60 * 1000},
    '90d':  {resolution: 86400,  spanMs: 90 * 24 * 60 * 60 * 1000},
    '365d': {resolution: 604800, spanMs: 365 * 24 * 60 * 60 * 1000}
}

/**
 * Reshape the raw `/metrics/current` wire payload (`{timestamp, values:{"weather.temperature":…}}`)
 * into the nested weather/climate snapshot the dashboard consumes. Missing/unavailable readings
 * stay `null`/`undefined` so cards fall back to their em-dash placeholder.
 * @param {import('./types.js').MetricsCurrentWire} raw
 * @return {import('./types.js').MetricsCurrentResponse}
 */
function toMetricsSnapshot(raw) {
    const values = raw?.values || {}
    return {
        timestamp: raw?.timestamp,
        weather: {
            temperature: values['weather.temperature'],
            humidity: values['weather.humidity'],
            pressure: values['weather.pressure'],
            windSpeed: values['weather.windSpeed'],
            windGust: values['weather.windGust'],
            windAngle: values['weather.windAngle'],
            light: values['weather.light'],
            rainfall: values['weather.rainfall']
        },
        climate: {
            temperatureInside: values['climate.temperature'],
            humidityInside: values['climate.humidity'],
            luminosity: values['climate.luminosity']
        }
    }
}

/**
 * @return {Promise<import('./types.js').MetricsCurrentResponse>}
 */
export async function getCurrentMetrics() {
    const raw = await request('GET', '/metrics/current')
    return toMetricsSnapshot(raw)
}

/**
 * @return {Promise<{metrics: string[]}>}
 */
export function listMetrics() {
    return request('GET', '/metrics')
}

/**
 * Rescale bucket timestamps to epoch milliseconds.
 *
 * `/metrics/:id/history` reports `t` as epoch **seconds** (see `metrics-store.aggregate`
 * in greenhouse-api), which `new Date()` would read as milliseconds and place in 1970.
 * Normalizing here keeps every consumer — chart, hero readout, tooltip — on one unit.
 *
 * @param {*} samples
 * @return {import('./types.js').MetricSample[]}
 */
function normalizeSamples(samples) {
    if (!Array.isArray(samples))
        return []
    return samples.map(sample => ({...sample, t: toEpochMs(sample?.t)}))
}

/**
 * @param {string} metricId
 * @param {import('./types.js').MetricRange} range
 * @return {Promise<import('./types.js').MetricsHistoryResponse>}
 */
export async function getMetricHistory(metricId, range) {
    const config = RANGE_MAP[range]
    if (!config)
        throw new Error(`Unsupported metric range: ${range}`)
    const to = new Date()
    const from = new Date(to.getTime() - config.spanMs)
    const qs = new URLSearchParams({
        resolution: String(config.resolution),
        from: from.toISOString(),
        to: to.toISOString()
    })
    const raw = await request('GET', `/metrics/${encodeURIComponent(metricId)}/history?${qs}`)
    return {...raw, samples: normalizeSamples(raw?.samples)}
}

export {RANGE_MAP, toMetricsSnapshot, normalizeSamples}
